import {
  Paper,
  Theme,
  ThemeOptions,
  Box
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { jaJP } from '@mui/x-data-grid/locales';
import {
  GridColDef,
  DataGridPremium,
  GridColumnResizeParams,
  GridRowParams,
  GridSortModel,
  GridFilterModel,
  GridFilterItem,
  useGridApiRef,
  GridRowSelectionModel,
  GridDensity
} from '@mui/x-data-grid-premium';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { format } from 'date-fns-tz';
import { useSnackbar } from 'notistack';
import { FC, memo, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { CustomLoadingOverlay } from 'src/components/utils/CustomLoadingOverlay';
import { appBarHeight } from 'src/constants/layout';
import LicenseContext from 'src/contexts/LicenseContext';
import { GarageEntity } from 'src/entities/garageEntity';
import { RansackPredicateEntity } from 'src/entities/RansackPredicate.entity';
import { useDeleteAllGaragesMutation } from 'src/hooks/useDeleteAllGarages.mutation';
import { queryKey, useGaragesRequest as usePaginationRequest } from 'src/hooks/useGarages.request';
import { useMutationGarage } from 'src/hooks/useQueryGarages';
import operatorValueUtil from 'src/utils/operatorValue.util';
import stringUtil from 'src/utils/string.util';

import { clearRowSelection } from '../common/clearRowSelection';
import { SelectAllSnackbars } from '../common/SelectAllSnackbars';

import { Columns } from './Columns';
import CustomToolbar from './CustomToolbar';
import FormDialog from './FormDialog';

interface CustomToolbarProps {
  toggleDialogIsOpen: () => void;
  onClickDeleteGarages: () => void;
  canDeleteAll: boolean;
  deleteAll: (settledFnk: () => void) => void;
  handleDownloadCSV: () => Promise<void>;
  density: GridDensity;
  onDensityChange: (density: GridDensity) => void;
}

const V2GaragesPresenter: FC = memo(() => {
  const theme: Theme & ThemeOptions = useTheme();
  const licenseContext = useContext(LicenseContext);
  const queryClient = useQueryClient();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(30);
  const [rowCount, setRowCount] = useState<number>(0);
  const [sortCondition, setSortCondition] = useState<string>('');
  const [orConditions, setOrConditions] = useState<RansackPredicateEntity[]>([]);
  const [andConditions, setAndConditions] = useState<RansackPredicateEntity[]>([]);
  const [searchCondition, setSearchCondition] = useState<string>('');
  const [editEntity, setEditEntity] = useState<GarageEntity|null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dialogIsOpen, setDialogIsOpen] = useState<boolean>(false);
  const [canDeleteAll, setCanDeleteAll] = useState<boolean>(false);
  const [density, setDensity] = useState<GridDensity>('standard');

  const { data: requestData, isLoading: requestIsLoading } = usePaginationRequest(page, sortCondition, searchCondition);
  const { data: allRequestData, refetch: refetchAllData } = usePaginationRequest(
    0,
    sortCondition,
    searchCondition,
    Number.MAX_SAFE_INTEGER,
    { enabled: false }
  );
  const { deleteGarages } = useMutationGarage();
  const { enqueueSnackbar } = useSnackbar();
  const { post } = useDeleteAllGaragesMutation();
  const apiRef = useGridApiRef();

  const deleteAll = (settledFnk: () => void) => {
    if (window.confirm(`選択された ${rowCount} 件の車庫を削除します。よろしいですか？`)) {
      setIsLoading(true);

      post.mutate(undefined, {
        onSuccess: () => {
          enqueueSnackbar('すべて削除しました');
        },
        onError: (error: AxiosError<{ message: string; }>) => {
          enqueueSnackbar(error.response?.data?.message);
        },
        onSettled: () => {
          void queryClient
            .invalidateQueries([queryKey])
            .finally(() => {
              settledFnk();
              setIsLoading(false);
            });
        }
      });
    }
  };

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (sortModel.length === 0) {
      setSortCondition('');
      return;
    }

    setSortCondition(
      [
        stringUtil.toSnakeCase(sortModel[0].field),
        sortModel[0].sort
      ].join('+')
    );
  };

  const onFilterChange = ((filterModel: GridFilterModel) => {
    const items = filterModel?.items;

    if (!items || items.length === 0) {
      setOrConditions([]);
      setAndConditions([]);
      return;
    }

    const ransackPredicates: RansackPredicateEntity[] = items.flatMap((item: GridFilterItem) => operatorValueUtil.convertToRansackPredicate(
      stringUtil.toSnakeCase(item.field),
      item.operator,
      item.value as string | string[] | undefined,
      filterModel.logicOperator
    ));

    setOrConditions(ransackPredicates.filter((predicate) => predicate?.link === 'or'));
    setAndConditions(ransackPredicates.filter((predicate) => predicate?.link === 'and'));
  });

  const openEditDialog = (ent: GarageEntity) => {
    setEditEntity(ent);
    setDialogIsOpen(true);
  };
  const openNewFormDialog = () => {
    setEditEntity(null);
    setDialogIsOpen(true);
  };

  const onColumnWidthChange = (params: GridColumnResizeParams) => {
    const columns = dataGridColumns.map((col) => {
      let newDefinition = { field: col.field, width: col.width };
      if (col.field === params.colDef.field) {
        newDefinition = { ...newDefinition, width: params.colDef.width };
      }
      return newDefinition;
    });
    localStorage.setItem('garage-columns-width', JSON.stringify(columns));
  };

  const onSelectionModelChange = (rowSelectionModel: GridRowSelectionModel) => {
    const ids = Array.from(rowSelectionModel.ids);
    if (rowSelectionModel.type === 'include') {
      setSelectedIds(ids.map((it) => Number(it)));
    } else if (rowSelectionModel.type === 'exclude') {
      const allIds = requestData.data.map((it) => it.id);
      setSelectedIds(allIds.filter((id) => !ids.includes(id)));
    }
  };

  const onClickDeleteGarages = () => {
    if (selectedIds.length === 0) {
      enqueueSnackbar('削除する車庫を選択してください');
      return;
    }

    if (window.confirm(`選択された ${selectedIds.length} 件の車庫を削除します。よろしいですか？`)) {
      setIsLoading(true);
      deleteGarages.mutateAsync(selectedIds)
        .then(() => clearRowSelection(apiRef, setSelectedIds))
        .catch((e: AxiosError<{ message: string; }>) => {
          enqueueSnackbar(e.response?.data?.message);
          setIsLoading(false);
        }).finally(() => {
          void queryClient
            .invalidateQueries([queryKey])
            .finally(() => {
              setIsLoading(false);
            });
        });
    }
    setDialogIsOpen(false);
  };

  const onRowDoubleClick = (params: GridRowParams) => {
    setEditEntity(params.row as GarageEntity);
    setDialogIsOpen(true);
  };

  const closeFormDialog = () => {
    setDialogIsOpen(false);
    setEditEntity(null);
  };

  const dataGridColumns: GridColDef[] = useMemo(() => Columns(openEditDialog)
    .filter((col) => {
      if (licenseContext?.config?.selectable_companies?.length > 1) {
        return true;
      }
      return col.field !== 'company_name';
    }), [licenseContext.config]);

  useEffect(() => {
    const columnWidth = localStorage.getItem('garage-columns-width');
    if (columnWidth) {
      const columns = JSON.parse(columnWidth) as unknown as { field: string, width: number}[];
      dataGridColumns.forEach((col) => {
        const saved = columns.find((e) => col.field === e.field);
        if (saved && saved.width) {
          col.flex = undefined;
          col.width = saved.width;
        }
      });
    }
  }, [dataGridColumns]);

  useEffect(() => {
    setSearchCondition(
      [
        operatorValueUtil.convertToQueryParams('or_conditions', orConditions),
        operatorValueUtil.convertToQueryParams('and_conditions', andConditions),
      ].filter((maybe) => maybe).join('&')
    );
  }, [andConditions, orConditions]);

  useEffect(() => {
    if (!requestData) return;

    setRowCount(requestData?.totalCount || 0);
  }, [requestData]);

  useEffect(() => {
    const currentPageRowCount = requestData?.data?.length ?? 0;
    const selectedAll =
      currentPageRowCount > 0 && selectedIds.length === currentPageRowCount;
    const canPaginate = rowCount > pageSize;

    // 複数ページあるときだけダイアログを表示（1ページのみなら両方の選択肢は同じなので不要）
    setCanDeleteAll(selectedAll && canPaginate);
  }, [selectedIds, requestData, rowCount, pageSize]);

  const handleDownloadCSV = async () => {
    setIsLoading(true);
    try {
      const options = {
        utf8WithBom: true,
        allColumns: true,
        fileName: `LOG-車庫一覧-${format(new Date(), 'yyyyMMddHHmmss', { timeZone: 'Asia/Tokyo' })}`
      };
      const { data: fetchedAllData } = await refetchAllData();

      apiRef.current.setRows(fetchedAllData.data);
      // apiRef.current.setRowSelectionModel(selectedIds);
      apiRef.current.exportDataAsCsv(options);
      apiRef.current.setRows(requestData.data);
    } catch (error) {
      console.error('CSV出力エラー:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>車庫管理</title>
      </Helmet>
      <Box p={1}>
        <FormDialog
          open={dialogIsOpen}
          onClose={closeFormDialog}
          entity={editEntity}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center'
          }}
        >
          <SelectAllSnackbars
            totalPageCount={requestData?.totalPageCount || 1}
            totalCount={requestData?.totalCount || 0}
            perPage={pageSize}
            allIds={allRequestData?.data.map((it) => it.id) || requestData?.allIds || []}
            selectedIds={selectedIds}
            apiRef={apiRef}
            setSelectedIds={setSelectedIds}
          />
          <Box flexGrow={1} sx={{ width: '100%', }}>
            <Paper
              style={{
                width: '100%',
                height: `calc(100vh - ${theme.spacing(4)} - ${appBarHeight}px)`
              }}
            >
              <DataGridPremium
                apiRef={apiRef}
                rows={requestData?.data || []}
                columns={dataGridColumns}
                pagination
                checkboxSelection
                disableRowGrouping
                showToolbar
                density={density}
                onDensityChange={setDensity}
                sx={{
                  backgroundColor: theme.colors.alpha.white[100],
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: theme.colors.alpha.white[100]
                  }
                }}
                slots={{
                  loadingOverlay: CustomLoadingOverlay,
                  toolbar: CustomToolbar
                }}
                slotProps={{
                  toolbar: {
                    toggleDialogIsOpen: openNewFormDialog,
                    onClickDeleteGarages,
                    canDeleteAll,
                    deleteAll,
                    handleDownloadCSV,
                    density,
                    onDensityChange: setDensity
                  } as Partial<CustomToolbarProps>
                }}
                localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
                loading={requestIsLoading || isLoading}
                onColumnWidthChange={(params) => onColumnWidthChange(params)}
                onRowSelectionModelChange={onSelectionModelChange}
                onRowDoubleClick={onRowDoubleClick}
                disableRowSelectionOnClick
                sortingMode="server"
                paginationMode="server"
                filterMode="server"
                paginationModel={{ page, pageSize }}
                onPaginationModelChange={(model) => {
                  setPage(model.page);
                  setPageSize(model.pageSize);
                  // ページ遷移時は選択をリセットする。state だけでなくグリッド内部の選択モデルも
                  // クリアしないと、全選択(exclude)が次ページへ引き継がれ選択カウントだけ残る。
                  clearRowSelection(apiRef, setSelectedIds);
                }}
                rowCount={rowCount}
                onSortModelChange={handleSortModelChange}
                onFilterModelChange={onFilterChange}
                keepNonExistentRowsSelected
                disableAggregation
                disablePivoting
              />
            </Paper>
          </Box>
        </Box>
      </Box>
    </>
  );
});

export default V2GaragesPresenter;
